/* eslint-disable @typescript-eslint/no-var-requires */
import {
  HeadersFunction,
  LoaderFunctionArgs,
  TypedResponse,
} from '@remix-run/node';
import {
  Form,
  Link,
  Outlet,
  json,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useRouteError,
  useSearchParams,
} from '@remix-run/react';
import {ErrorBoundaryComponent} from '@remix-run/react/dist/routeModules';
import {captureRemixErrorBoundaryError} from '@sentry/remix';
import {AutoComplete} from 'primereact/autocomplete';
import {Button} from 'primereact/button';
import {Dropdown} from 'primereact/dropdown';
import {InputText} from 'primereact/inputtext';
import {Message} from 'primereact/message';
import * as React from 'react';
import MainHeading from '~/components/MainHeading';
import Panel from '~/components/Panel';
import PublicLoadResult from '~/types/PublicLoadResult';
import {filterStates} from '~/utils/constants';
import {BASE_URL_CWP} from '~/utils/environment';
import {CityState} from './cityAutocomplete';
import * as gtag from '~/utils/gtags.client';

type TrackerFormResponse = {
  loads: PublicLoadResult[];
  errors: Record<string, string>;
};

const fieldOptions = [
  {label: 'Order Number', value: 'O'},
  {label: 'Purchase Order Number', value: 'P'},
  {label: 'Bill of Lading Number', value: 'B'},
  {label: 'Consignee Reference Number', value: 'C'},
];

export async function loader({
  request,
}: LoaderFunctionArgs): Promise<TypedResponse<TrackerFormResponse>> {
  const searchParams = new URL(request.url);
  const destination = searchParams.searchParams.get('destination') as string;
  const type = searchParams.searchParams.get('type') as string;
  const value = searchParams.searchParams.get('value') as string;

  const errors: Record<string, string> = {};

  if (!request.url.includes('/results')) {
    return json({loads: [], errors});
  }

  if (!destination && !type && !value) {
    errors.required = 'All fields required';
    return json({loads: [], errors});
  }

  if (type === 'O' && value.length > 8) {
    errors.failed = 'Invalid Order Number';
    return json({loads: [], errors});
  }

  try {
    const a = performance.now();
    const loadsResponse = await fetch(
      `${BASE_URL_CWP}/tracking/search?${new URLSearchParams({
        destination,
        type,
        value,
      })}`,
    );
    console.log(
      `response: ${loadsResponse.status}, ${performance.now() - a}ms`,
    );
    if (loadsResponse.status >= 400 && loadsResponse.status !== 404) {
      errors.failed = 'Could not load data.';
      return json({loads: [], errors});
    }
    if (loadsResponse.status === 404) {
      const option = fieldOptions.find(o => o.value === type);
      errors.failed = `${option?.label} ${value} Not Found`;
      return json({loads: [], errors});
    }

    const loads = (await loadsResponse.json()).map((l: PublicLoadResult) =>
      filterStates.includes(l.transitState)
        ? l
        : {
            ...l,
            bol: null,
            poNumber: null,
            consigneeRefNumber: null,
            pickupNumber: null,
          },
    );
    return json(
      {loads, errors},
      {headers: {'Cache-Control': 'public, max-age=300'}},
    );
  } catch (ex) {
    console.error(ex);
    errors.failed = 'Could not load data.';
    return json({loads: [], errors});
  }
}

export const headers: HeadersFunction = () => ({
  'Cache-Control': 'max-age=300 s-maxage=300 ',
});

export const ErrorBoundary: ErrorBoundaryComponent = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return (
    <Panel className="max-w-screen-md flex flex-col items-center justify-center">
      <Message severity="error" text="An unknown error occurred." />
      <Link className="max-w-36 my-2 self-center" to="/">
        Go back to search
      </Link>
    </Panel>
  );
};

export default function TrackerForm() {
  const {loads, errors} = useLoaderData<typeof loader>();
  const cityFetcher = useFetcher<{values: CityState[]}>();
  const data = cityFetcher.data;

  const navigation = useNavigation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [selectedType, setSelectedType] = React.useState('O');
  const [value, setValue] = React.useState('');
  const [destination, setDestination] = React.useState('');
  const [showValidationError, setShowValidationError] = React.useState(false);
  const [requestError, setRequestError] = React.useState('');
  const [showErrors, setShowErrors] = React.useState(false);

  const suggestions = React.useMemo(
    () =>
      destination.length >= 3 && data?.values
        ? data.values.map(item => `${item.city}, ${item.state}`)
        : [],
    [data?.values, destination],
  );

  const onInvalid = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowValidationError(true);
    setRequestError('');
    setShowErrors(true);
  };

  React.useEffect(() => {
    if (sessionStorage.getItem('search') === 'true') {
      const companies = new Set(loads.map(load => load.shipperName));
      const uniqueCompanies = Array.from(companies);
      const event: Record<string, string> = {
        action: 'search_results',
        count: loads.length.toString(),
        company: uniqueCompanies.join(', '),
        company_count: uniqueCompanies.length.toString(),
      };
      if (errors.failed || errors.required) {
        event.error = errors.failed || errors.required;
      }
      gtag.event(event);
      sessionStorage.removeItem('search');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loads]);

  React.useEffect(() => {
    if (location.pathname === '/results') {
      if (loads.length === 1) {
        navigate(`/results/${loads[0].loadNumber}?${searchParams.toString()}`, {
          replace: true,
        });
      }
      if (loads.length === 0) {
        navigate(`/?${searchParams.toString()}`, {
          replace: true,
        });
      }
    }
  }, [loads, location, searchParams, navigate]);

  const onSubmit = () => {
    sessionStorage.setItem('search', 'true');
    const fieldOption = fieldOptions.filter(
      option => option.value === selectedType,
    );
    gtag.event({
      action: 'search',
      selected_type: fieldOption?.[0]?.label,
      destination,
    });
    setShowValidationError(false);
  };

  const onClear = () => {
    setShowValidationError(false);
    setDestination('');
    setSelectedType('O');
    setValue('');
    setRequestError('');
    navigate('/');
    errors.failed = '';
  };

  return (
    <div className="md:py-4 md:px-4 w-full flex flex-col items-center">
      {loads.length === 0 ? (
        <Panel>
          <div className="mb-4">
            <MainHeading>Welcome to the ATS Load Tracker</MainHeading>
          </div>
          <Form
            action="/results"
            method="GET"
            onSubmit={onSubmit}
            onInvalid={onInvalid}>
            <p className="pb-2">
              Thank you for trusting ATS when moving your precious freight.
              Real-time tracking and documentation are also available using our{' '}
              <a href="https://customer.ats-inc.com" target="_parent">
                Customer Portal
              </a>
              .
            </p>
            {showValidationError && navigation.state !== 'loading' && (
              <Message
                className="w-full justify-start"
                severity="error"
                text="All fields are required"
              />
            )}
            {(requestError || errors.failed) &&
              !showValidationError &&
              navigation.state !== 'loading' && (
                <div className="mb-2">
                  <Message
                    className="mb-2"
                    severity="error"
                    text={requestError || errors.failed}
                  />
                  <p>
                    Make sure you’ve entered the correct reference type or
                    number. Still having trouble? Contact us at{' '}
                    <a href="tel:8006338287">1-800-633-8287</a>.
                  </p>
                </div>
              )}
            <div className="sm:flex sm:gap-2">
              <Dropdown
                className="sm:my-2 my-2 w-full sm:w-4/12"
                style={{height: '42px'}}
                options={fieldOptions}
                name="type"
                defaultValue="O"
                value={selectedType}
                ariaLabel="Tracking Number Type"
                aria-label="Tracking Number Type"
                onChange={e => setSelectedType(e.value)}
              />
              <InputText
                className="sm:mb-2 my-2 w-full sm:w-8/12"
                name="value"
                value={value}
                onChange={e => setValue(e.target.value)}
                autoComplete={selectedType}
                placeholder="Enter Number Here"
                required
                invalid={showErrors && !value}
              />
            </div>
            <label className="font-bold" htmlFor="destination">
              Delivery Location
            </label>
            <AutoComplete
              className="mt-2 w-full"
              inputStyle={{width: '100%'}}
              name="destination"
              invalid={showErrors && !destination}
              placeholder="Enter Destination City or Zip"
              value={destination}
              suggestions={suggestions}
              completeMethod={e => {
                if (e.query.length < 3) {
                  return;
                }
                gtag.event({
                  action: 'autocomplete',
                  search_term: e.query,
                });
                cityFetcher.submit(
                  {guess: e.query},
                  {action: '/cityAutocomplete', method: 'GET'},
                );
              }}
              onChange={e => setDestination(e.value)}
              required
            />
            {/* This is just used to force the page to search again even if the user doesn't change the form values. */}
            <InputText
              className="hidden"
              name="q"
              value={searchParams.get('q') === '0' ? '1' : '0'}
            />
            <div className="flex flex-row gap-2 mt-4">
              <Button
                loading={navigation.state === 'loading'}
                type="submit"
                className="justify-center w-full md:w-1/2">
                Track
              </Button>
              <Button
                type="reset"
                onClick={onClear}
                severity="secondary"
                className="justify-center w-full md:w-1/2">
                Clear
              </Button>
            </div>
          </Form>
        </Panel>
      ) : (
        <Outlet context={loads} />
      )}
    </div>
  );
}
